import type { EnhancedAsset, PortfolioResponse } from '@bitte-ai/types';
import { TokenRow } from './TokenRow';

export function TokenList({ data }: { data: PortfolioResponse }) {
  // Check if data is PortfolioData
  if (!('significantAssets' in data)) {
    return null;
  }

  // Sort significantAssets by price in descending order
  const sortedAssets = [...data.significantAssets].sort(
    (a: EnhancedAsset, b: EnhancedAsset) => {
      const priceA = a.priceData?.day?.[0]?.[1] ?? 0;
      const priceB = b.priceData?.day?.[0]?.[1] ?? 0;
      return priceB - priceA;
    },
  );

  const columns = [
    { id: 'rank', label: '#', show: true },
    { id: 'token', label: 'Token', show: true },
    {
      id: 'price',
      label: 'Price',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) =>
          asset.priceData?.day && asset.priceData.day.length > 0,
      ),
    },
    {
      id: 'balance',
      label: 'Balance',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.balances?.balance !== undefined,
      ),
    },
    {
      id: 'value',
      label: 'Value',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.usdValue !== undefined,
      ),
    },
    {
      id: 'volume24h',
      label: '24h Volume',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.volume24h !== undefined,
      ),
    },
    {
      id: 'marketcap',
      label: 'Market Cap',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.marketcap !== undefined,
      ),
    },
    {
      id: 'change',
      label: 'Change',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.priceChanges !== undefined,
      ),
    },
    {
      id: 'chart',
      label: 'Chart',
      show: data.significantAssets.some(
        (asset: EnhancedAsset) => asset.priceData !== undefined,
      ),
    },
  ];

  const visibleColumns = columns.filter((col) => col.show);

  return (
    <div className="bitte-overflow-x-auto">
      <div className="bitte-min-w-[1000px] bitte-max-w-[1000px] bitte-mx-auto bitte-space-y-4">
        {/* Header row */}
        <div className="bitte-p-2 bitte-custom-scrollbar lg:bitte-p-4">
          <div
            className="bitte-grid bitte-items-center bitte-uppercase bitte-font-medium bitte-text-sm bitte-text-muted-foreground"
            style={{
              gridTemplateColumns: `30px ${visibleColumns
                .slice(1)
                .map((col) => {
                  if (col.id === 'token') return '1.5fr';
                  if (col.id === 'balance') return '1.5fr';
                  return '1fr';
                })
                .join(' ')}`,
            }}
          >
            {visibleColumns.map((col) => (
              <div key={col.id} className="bitte-text-[12px]">
                {col.label}
              </div>
            ))}
          </div>
        </div>

        {/* Token rows */}
        <div className="bitte-space-y-2">
          {sortedAssets.map((token, index) => (
            <TokenRow
              key={token.meta?.contractAddress}
              rank={index + 1}
              token={token}
              visibleColumns={visibleColumns}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
